import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { FormCheckbox } from '@/components/form/FormCheckbox'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormSelect } from '@/components/form/FormSelect'
import { FormTextarea } from '@/components/form/FormTextarea'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { ScreeningQuestion } from '@/features/recruiter/api/jobs.api'
import { useScreeningQuestionMutations } from '@/features/recruiter/hooks/useJobs'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { humanizeEnum } from '@/utils/format'

const QUESTION_TYPE_OPTIONS = [
  { value: 'text', label: 'Text (free answer)' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple Choice (radio buttons)' },
  { value: 'dropdown', label: 'Dropdown (select menu)' },
]

const TYPES_WITH_OPTIONS = ['multiple_choice', 'dropdown'] as const
type TypeWithOptions = (typeof TYPES_WITH_OPTIONS)[number]

const hasOptions = (t: string): t is TypeWithOptions =>
  (TYPES_WITH_OPTIONS as readonly string[]).includes(t)

const schema = z
  .object({
    question_text: z.string().min(1, 'Question text is required').max(1000),
    question_type: z.enum(['text', 'yes_no', 'multiple_choice', 'dropdown']),
    is_required: z.boolean(),
    sort_order: z.string().optional(),
    options: z.array(z.object({ value: z.string().min(1, 'Option text is required').max(200) })),
  })
  .superRefine((data, ctx) => {
    if (hasOptions(data.question_type) && data.options.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Add at least 2 options for this question type.',
        path: ['options'],
      })
    }
  })

type FormValues = z.infer<typeof schema>

function toFormValues(item?: ScreeningQuestion, nextSortOrder = 0): FormValues {
  return {
    question_text: item?.questionText ?? '',
    question_type: item?.questionType ?? 'text',
    is_required: item?.isRequired ?? true,
    sort_order: item != null ? String(item.sortOrder) : String(nextSortOrder),
    options: (item?.options ?? []).map((v) => ({ value: v })),
  }
}

export function JobScreeningQuestionsSection({
  jobId,
  questions,
}: {
  jobId: number
  questions: ScreeningQuestion[]
}) {
  const [editing, setEditing] = useState<ScreeningQuestion | 'new' | null>(null)
  const [apiError, setApiError] = useState<unknown>(null)
  const { create, update, remove } = useScreeningQuestionMutations(jobId)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues() })

  const { fields, append, remove: removeOption } = useFieldArray({ control, name: 'options' })

  const questionType = useWatch({ control, name: 'question_type' })
  const showOptions = hasOptions(questionType)

  useEffect(() => {
    reset(
      toFormValues(
        editing === 'new' || editing === null ? undefined : editing,
        questions.length,
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, reset])

  const isPending = create.isPending || update.isPending

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    const payload = {
      question_text: values.question_text,
      question_type: values.question_type,
      is_required: values.is_required,
      sort_order: values.sort_order ? Number(values.sort_order) : undefined,
      options: hasOptions(values.question_type) ? values.options.map((o) => o.value) : undefined,
    }
    const onSettled = { onSuccess: () => setEditing(null), onError: (e: unknown) => setApiError(e) }
    if (editing === 'new') {
      create.mutate(payload, onSettled)
    } else if (editing) {
      update.mutate({ id: editing.id, payload }, onSettled)
    }
  })

  return (
    <div>
      <div className="alert alert-info py-2 fs-13 mb-3" role="note">
        <strong>Question types:</strong>
        <ul className="mb-0 mt-1">
          <li><strong>Text</strong> — candidate types a free-text answer.</li>
          <li><strong>Yes / No</strong> — candidate selects Yes or No.</li>
          <li>
            <strong>Multiple Choice</strong> — you define 2–20 options; candidate picks one via
            radio buttons. Example: <em>"Which shift are you available for?" → Morning / Evening /
            Night</em>.
          </li>
          <li>
            <strong>Dropdown</strong> — same as Multiple Choice but shown as a compact dropdown
            menu. Use when you have many options or limited screen space.
          </li>
        </ul>
      </div>

      {questions.length === 0 && (
        <p className="text-muted fs-13">No screening questions added yet.</p>
      )}
      <div className="d-flex flex-column gap-2 mb-3">
        {questions
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((question) => (
            <div
              key={question.id}
              className="d-flex align-items-start justify-content-between border rounded p-2"
            >
              <div>
                <p className="mb-0 fs-14">{question.questionText}</p>
                <p className="mb-0 fs-12 text-muted">
                  {humanizeEnum(question.questionType)}
                  {question.isRequired ? ' · Required' : ' · Optional'}
                </p>
                {question.options.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {question.options.map((opt) => (
                      <span key={opt} className="badge bg-light text-dark border fs-11">
                        {opt}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="d-flex gap-2 flex-shrink-0 ms-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setEditing(question)}
                >
                  Edit
                </button>
                <Button
                  variant="danger"
                  className="btn-sm"
                  confirmTitle="Remove this screening question?"
                  onClick={() => remove.mutate(question.id)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
      </div>
      <Button variant="secondary" onClick={() => setEditing('new')}>
        Add Screening Question
      </Button>

      <Modal
        show={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Screening Question' : 'Edit Screening Question'}
      >
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(apiError)}
          generalMessage={apiError ? getErrorMessage(apiError) : null}
        />
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormTextarea
            control={control}
            name="question_text"
            label="Question"
            maxLength={1000}
            required
          />
          <FormSelect
            control={control}
            name="question_type"
            label="Answer Type"
            options={QUESTION_TYPE_OPTIONS}
            required
          />

          {showOptions && (
            <div className="mb-3">
              <label className="form-label fs-13 fw-medium">
                Options <span className="text-danger">*</span>
                <span className="text-muted fw-normal ms-1">(min 2, max 20)</span>
              </label>
              {errors.options?.root?.message && (
                <div className="text-danger fs-12 mb-1">{errors.options.root.message}</div>
              )}
              {'message' in (errors.options ?? {}) && (
                <div className="text-danger fs-12 mb-1">
                  {(errors.options as { message?: string })?.message}
                </div>
              )}
              <div className="d-flex flex-column gap-1 mb-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="d-flex gap-2 align-items-center">
                    <FormInput
                      control={control}
                      name={`options.${index}.value`}
                      label=""
                      placeholder={`Option ${index + 1}`}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger flex-shrink-0"
                      onClick={() => removeOption(index)}
                      aria-label="Remove option"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              {fields.length < 20 && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => append({ value: '' })}
                >
                  + Add option
                </button>
              )}
            </div>
          )}

          <FormInput control={control} name="sort_order" label="Sort Order" type="number" />
          <FormCheckbox control={control} name="is_required" label="Required" />
          <Button type="submit" className="w-100" loading={isPending}>
            Save
          </Button>
        </form>
      </Modal>
    </div>
  )
}
